export class OTPRateLimiter {
    constructor(config = {}) {
        this.attempts = new Map();
        this.maxAttempts = config.maxAttempts ?? 5;
        this.windowMs = config.windowMs ?? 15 * 60 * 1000; // 15 minutes
        this.lockoutMs = config.lockoutMs ?? 30 * 60 * 1000; // 30 minutes
    }
    /**
     * Check if an OTP attempt is allowed for the given identifier
     */
    canAttempt(identifier) {
        const now = Date.now();
        const record = this.attempts.get(identifier);
        if (!record) {
            return { allowed: true, remainingAttempts: this.maxAttempts };
        }
        // Lockout check first: a locked record must persist until lockoutMs has
        // elapsed from lockedAt, even when that exceeds windowMs. Otherwise the
        // window-reset below would silently clip effective lockout to windowMs.
        if (record.lockedAt !== undefined) {
            const lockoutRemaining = this.lockoutMs - (now - record.lockedAt);
            if (lockoutRemaining > 0) {
                return {
                    allowed: false,
                    remainingAttempts: 0,
                    lockoutRemainingMs: lockoutRemaining,
                };
            }
            this.attempts.delete(identifier);
            return { allowed: true, remainingAttempts: this.maxAttempts };
        }
        if (now - record.lastAttempt > this.windowMs) {
            this.attempts.delete(identifier);
            return { allowed: true, remainingAttempts: this.maxAttempts };
        }
        return {
            allowed: true,
            remainingAttempts: this.maxAttempts - record.count,
        };
    }
    /**
     * Record an OTP attempt for the given identifier
     */
    recordAttempt(identifier) {
        const now = Date.now();
        const record = this.attempts.get(identifier);
        if (!record) {
            this.attempts.set(identifier, { count: 1, lastAttempt: now });
            return;
        }
        const nextCount = record.count + 1;
        const next = {
            count: nextCount,
            lastAttempt: now,
            lockedAt: record.lockedAt ?? (nextCount >= this.maxAttempts ? now : undefined),
        };
        this.attempts.set(identifier, next);
    }
    /**
     * Clear attempts on successful verification
     */
    clearAttempts(identifier) {
        this.attempts.delete(identifier);
    }
    /**
     * Reset all tracked attempts
     */
    reset() {
        this.attempts.clear();
    }
    /**
     * Format lockout remaining time for display
     */
    formatLockoutTime(ms) {
        const minutes = Math.ceil(ms / 60000);
        if (minutes === 1) {
            return "1 minute";
        }
        return `${minutes} minutes`;
    }
    /**
     * Get the number of attempts made for an identifier
     */
    getAttemptCount(identifier) {
        return this.attempts.get(identifier)?.count ?? 0;
    }
}
/** Default singleton instance */
export const otpRateLimiter = new OTPRateLimiter();
